import {
  Component,
  DestroyRef,
  ElementRef,
  ViewEncapsulation,
  afterNextRender,
  computed,
  effect,
  inject,
  Injector,
  input,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TooltipDirective } from '../../../tooltip/tooltip.directive';
import { LogoComponent } from '../../../logo/logo.component';
import { deepActiveElement, restoreFocus, trapTabKey } from '../../../modal/focus-trap';
import { injectNarrowViewport } from '../breakpoint';

let nextSidenavId = 0;

/**
 * The cabinet rail. Wide viewports show it beside the page, collapsible to
 * icons. Narrow viewports show it as a modal drawer: it traps focus, closes
 * on Escape, on the scrim and after navigating, and returns focus to the
 * control that opened it.
 */
@Component({
  selector: 'app-sidenav-cabinet',
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [TooltipDirective, LogoComponent],
  host: {
    '[class.mobile-open]': 'drawerOpen()',
    '[class.collapsed]': 'effectiveCollapsedState',
  },
})
export class SidenavCabinetComponent {
  readonly isCollapsed = input<boolean>(false);
  readonly isOpen = input<boolean>(false);
  /** Accessible name of the navigation landmark. */
  readonly navLabel = input('Main');

  readonly toggleSidebar = output<void>();
  readonly closeSidebar = output<void>();

  /** id of the rail, for aria-controls on the controls that open or collapse it. */
  readonly railId = `cabinet-rail-${nextSidenavId++}`;

  readonly narrow = injectNarrowViewport();
  readonly drawerOpen = computed(() => this.narrow() && this.isOpen());
  readonly collapsed = computed(() => !this.narrow() && this.isCollapsed());

  private readonly rail = viewChild.required<ElementRef<HTMLElement>>('rail');
  private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton');
  private readonly injector = inject(Injector);
  private returnFocusTo: HTMLElement | null = null;

  /** Kept for templates that read the collapsed state directly. */
  get effectiveCollapsedState(): boolean {
    return this.collapsed();
  }

  constructor() {
    effect(() => {
      const open = this.drawerOpen();
      untracked(() => (open ? this.onDrawerOpened() : this.onDrawerClosed()));
    });

    inject(Router)
      .events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => {
        if (this.drawerOpen()) this.closeSidebar.emit();
      });
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  onCloseSidebar(): void {
    this.closeSidebar.emit();
  }

  onRailKeydown(event: KeyboardEvent): void {
    if (!this.drawerOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onCloseSidebar();
      return;
    }
    trapTabKey(event, this.rail().nativeElement);
  }

  private onDrawerOpened(): void {
    const active = deepActiveElement();
    this.returnFocusTo = active && active !== document.body ? active : null;
    afterNextRender(() => this.closeButton()?.nativeElement.focus(), { injector: this.injector });
  }

  private onDrawerClosed(): void {
    const target = this.returnFocusTo;
    this.returnFocusTo = null;
    if (target) restoreFocus(target);
  }
}
